import React, { useState, useEffect } from 'react';
import { VehicleType, TrailerType } from '../types';
import VehicleIcon from './VehicleIcon';
import TrailerIcon from './TrailerIcon';
import { useLanguage } from '../contexts/LanguageContext';
import useTranslate from '../hooks/useTranslate';

interface VehicleCatalogDisplayProps {
  selectedVehicleType: VehicleType | '';
  selectedTrailerType: TrailerType | null;
  onVehicleSelect: (type: VehicleType) => void;
  onTrailerSelect: (type: TrailerType | null) => void;
}

// Component to display a visualization of selected vehicle and trailer combination
const VehicleCatalogDisplay: React.FC<VehicleCatalogDisplayProps> = ({
  selectedVehicleType,
  selectedTrailerType,
  onVehicleSelect,
  onTrailerSelect,
}) => {
  // Get translation functions
  const { t, language } = useLanguage();
  const { translateVehicleType, translateTrailerType } = useTranslate();
  
  // State to force re-render when language changes
  const [, setRenderKey] = useState<number>(0);
  
  // State to track if user is on mobile device
  const [isMobile, setIsMobile] = useState<boolean>(false);
  
  // Function to check if device is mobile based on window width
  const checkIsMobile = () => {
    return window.innerWidth < 768; // Tailwind's md breakpoint
  };
  
  // Initialize mobile detection and set up resize listener
  useEffect(() => {
    // Set initial value
    setIsMobile(checkIsMobile());
    
    // Handle window resize
    const handleResize = () => {
      setIsMobile(checkIsMobile());
    };
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      // Increment key to force re-render
      setRenderKey(prev => prev + 1);
    };
    
    // Add event listener
    window.addEventListener('language-changed', handleLanguageChange);
    
    // Clean up
    return () => {
      window.removeEventListener('language-changed', handleLanguageChange);
    };
  }, []);

  // Helper function to determine if a vehicle type is a full unit
  const isFullUnit = (type: VehicleType): boolean => {
    return [
      VehicleType.FULL_6_AXLES,
      VehicleType.FULL_7_AXLES,
      VehicleType.FULL_8_AXLES,
      VehicleType.FULL_9_AXLES
    ].includes(type);
  };

  // Helper function to determine if a vehicle can have a trailer
  const canHaveTrailer = (type: VehicleType | ''): boolean => {
    return type === VehicleType.SINGLE_WITH_2_AXLE_TRAILER || 
           type === VehicleType.SINGLE_WITH_3_AXLE_TRAILER ||
           isFullUnit(type as VehicleType);
  };

  // Get all vehicle types
  const allVehicleTypes = Object.values(VehicleType);

  return (
    <div className="mb-6">
      {/* Vehicle Types Section */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-2 sm:mb-3">
          {t('quotationForm.vehicleType')}
        </h3>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 xs:gap-3 sm:gap-4">
          {allVehicleTypes.map((type) => (
            <div 
              key={type} 
              className={`
                flex flex-col items-center justify-center p-4 rounded-lg cursor-pointer transition-all
                min-h-[120px] xs:min-h-[130px]
                ${selectedVehicleType === type 
                  ? 'bg-primary bg-opacity-10 border-2 border-primary shadow-md' 
                  : 'bg-white border border-gray-200 hover:border-primary hover:shadow'}
              `}
              onClick={() => onVehicleSelect(type)}
              data-vehicle-selection
            >
              <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mb-2 sm:mb-3">
                <VehicleIcon 
                  type={type} 
                  size={isMobile ? 48 : 64}
                  className={selectedVehicleType === type ? 'text-primary' : 'text-gray-600'} 
                />
              </div>
              <span className="text-xs sm:text-sm text-center font-medium leading-tight">
                {translateVehicleType(type)}
              </span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Trailer Types Section - Only show if vehicle can have trailer */}
      {canHaveTrailer(selectedVehicleType) && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2 sm:mb-3">
            {t('quotationForm.trailerType')}
          </h3>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 xs:gap-3 sm:gap-4">
            {/* No Trailer option */}
            <div 
              className={`
                flex flex-col items-center justify-center p-4 rounded-lg cursor-pointer transition-all
                min-h-[120px] xs:min-h-[130px]
                ${selectedTrailerType === null 
                  ? 'bg-primary bg-opacity-10 border-2 border-primary shadow-md' 
                  : 'bg-white border border-gray-200 hover:border-primary hover:shadow'}
              `}
              onClick={() => onTrailerSelect(null)}
              data-trailer-selection
            >
              <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mb-2 sm:mb-3">
                <TrailerIcon 
                  type={null} 
                  size={isMobile ? 48 : 64}
                  className={selectedTrailerType === null ? 'text-primary' : 'text-gray-600'} 
                />
              </div>
              <span className="text-xs sm:text-sm text-center font-medium leading-tight">
                {t('quotationForm.noTrailer')}
              </span>
            </div>
            
            {/* All trailer types */}
            {Object.values(TrailerType).map((type) => (
              <div 
                key={type}
                className={`
                  flex flex-col items-center justify-center p-3 sm:p-4 rounded-lg cursor-pointer transition-all
                  min-h-[120px] xs:min-h-[130px]
                  ${selectedTrailerType === type 
                    ? 'bg-primary bg-opacity-10 border-2 border-primary shadow-md' 
                    : 'bg-white border border-gray-200 hover:border-primary hover:shadow'}
                `}
                onClick={() => onTrailerSelect(type)}
                data-trailer-selection
              >
                <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mb-2 sm:mb-3">
                  <TrailerIcon 
                    type={type} 
                    size={isMobile ? 48 : 64}
                    className={selectedTrailerType === type ? 'text-primary' : 'text-gray-600'} 
                  />
                </div>
                <span className="text-xs sm:text-sm text-center font-medium leading-tight">
                  {translateTrailerType(type)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleCatalogDisplay;